import React, { useCallback, useState } from 'react';
import FilterPlateFormContext from 'contexts/FilterPlateFormContext';

import useOnRemoved from './hooks/useOnRemoved';
import useOnDrillDownConfirmed from './hooks/useOnDrillDownConfirmed';
import useOnVariableFilterAdded from './hooks/useOnVariableFilterAdded';
import FilterPlateForm from './FilterPlateForm';
import useOnSavedFilterAdded from './hooks/useOnSavedFilterAdded';
import useOnExcludeTermedDriversAdded from './hooks/useOnExcludeTermedDriversAdded';

const FilterPlateFormContainer = ({
  close,
  drillDown,
  isScope,
  onUpdated,
  onManualFilterChanged,
  onFilterAdded,
  isDateScopeDisabled,
  initialField,
  onFieldPicked,
}: {
  close: () => void;
  drillDown?: FixedFilter;
  isScope: boolean;
  onUpdated?: (newDrillDown: FixedFilter) => void;
  onManualFilterChanged?: () => void;
  onFilterAdded?: () => void;
  isDateScopeDisabled?: boolean;
  // Opens straight on value selection for this field, without a way back to
  // the field list (used by report pinned filters)
  initialField?: ReportPinnedFilters.Field;
  // Field-only mode: picking a field calls this and closes, skipping value
  // selection
  onFieldPicked?: (field: FleetOps.Field | DateBucketField) => void;
}) => {
  const [isEditing] = useState<boolean>(!!drillDown);
  const [field, setField] = useState<string | undefined>(
    drillDown ? drillDown.field : initialField?.field,
  );
  const [fieldType, setFieldType] = useState<FleetOps.FieldType | undefined>(
    drillDown ? drillDown.fieldType : initialField?.fieldType,
  );
  const [dataset, setDataset] = useState(
    drillDown ? drillDown.dataset : initialField?.dataset,
  );

  const stage =
    isEditing || (field && fieldType) ? 'valueSelection' : 'fieldSelection';
  const onDrillDownConfirmed = useOnDrillDownConfirmed({
    close,
    drillDown,
    isScope,
    onUpdated,
    onManualFilterChanged,
    onFilterAdded,
    isEditing,
    stage,
  });
  const onVariableFilterAdded = useOnVariableFilterAdded({
    close,
    drillDown,
    isScope,
  });
  const onSavedFilterAdded = useOnSavedFilterAdded({
    close,
    drillDown,
    isScope,
  });
  const onExcludeTermedDriversAdded = useOnExcludeTermedDriversAdded({
    close,
    isScope,
  });
  const onRemoved = useOnRemoved({
    isScope,
    drillDown,
  });
  const reset = useCallback(() => {
    setField(undefined);
    setFieldType(undefined);
  }, []);

  const onFieldSelected = useCallback(
    (field: FleetOps.Field | DateBucketField) => {
      if (onFieldPicked) {
        onFieldPicked(field);
        close();
        return;
      }

      setField(field.field);
      setFieldType(field.type);

      if (field.dataType) {
        setDataset(field.dataType);
      }
    },
    [close, onFieldPicked],
  );

  return (
    <FilterPlateFormContext.Provider
      value={{
        isEditing,
        stage,
        field,
        fieldType,
        dataset,
        drillDown,
        onFieldSelected,
        onDrillDownConfirmed,
        reset,
        onRemoved,
        onVariableFilterAdded,
        onSavedFilterAdded: onSavedFilterAdded,
        onExcludeTermedDriversAdded,
        isVariableFilterValuePicker: !!onUpdated,
        isScope,
        isDateScopeDisabled,
        isFieldLocked: !!initialField,
        isFieldOnly: !!onFieldPicked,
      }}
    >
      <FilterPlateForm stage={stage} fieldType={fieldType} />
    </FilterPlateFormContext.Provider>
  );
};

export default FilterPlateFormContainer;
